import { ENTITY_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
import { t } from '@/ui/i18n/i18n';
import { Ref } from 'vue';
import { PaginationResetCallback, SelectOptions } from '@/ui/types';
import { tagFilter } from './tagFilter';
import { dateRangeFilter as baseDateRangeFilter } from './dateFilter';
import { textFilter } from './textFilter';
import { checkboxFilter } from './checkboxFilter';
import { freeTagFilter } from './freeTagFilter';
import {
  inspectionOverdueOptions,
  inspectionStatusOptions,
  issueOverdueOptions,
  issuePriorityOptions,
  issueStatusOptions,
  issueTypeOptions,
  todoOverdueOptions,
  checklistStatusOptions,
} from '@/ui/components/filters/options';
import { dropdownFilter } from '@/ui/components/filters/dropdownFilter';
import { checklistAutoComplete } from '@/ui/components/checklist/ChecklistAutoComplete';

const dummyCallback = () => {
};
const horizontal = true;
const hideLabel = true;
const isStackable = true;

/**
 * @deprecated use horizontal/SearchFilter.vue instead
 */
export const searchFilter = (
  value: Ref<string>,
  reset: PaginationResetCallback = dummyCallback,
) => textFilter({
  value,
  name: 'search',
  label: t('common.search'),
  placeholder: t('form.enter-search'),
  reset,
  hideLabel,
  horizontal,
});

export const dateRangeFilter = (
  start: Ref<Date | null>,
  end: Ref<Date | null>,
  reset: PaginationResetCallback = dummyCallback,
  rangeLimitMonths?: number,
  placeHolder?: string,
  clearable = false,
) => baseDateRangeFilter({
  start,
  end,
  name: 'date',
  label: t('common.date'),
  placeholder: placeHolder || t('form.date-range'),
  clearable,
  reset,
  rangeLimitMonths,
  hideLabel,
  horizontal,
});

export const lastUpdatedFilter = (
  start: Ref<Date | null>,
  end: Ref<Date | null>,
  reset: PaginationResetCallback = dummyCallback,
) => baseDateRangeFilter({
  start,
  end,
  name: 'lastUpdated',
  label: t('common.last-updated'),
  placeholder: t('form.date-range'),
  clearable: true,
  reset,
  hideLabel,
  horizontal,
});

export const createdAtFilter = (
  start: Ref<Date | null>,
  end: Ref<Date | null>,
  reset: PaginationResetCallback = dummyCallback,
) => baseDateRangeFilter({
  start,
  end,
  name: 'createdAt',
  label: t('common.created-at'),
  placeholder: t('form.date-range'),
  clearable: true,
  reset,
  hideLabel,
  horizontal,
});

export const approvalStatusFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'approvalStatus',
  label: t('common.approval-status'),
  placeholder: t('common.approval-status'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const entityItemFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'entityItem',
  label: t('common.entity-item'),
  placeholder: t('common.entity-item'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
  maxListedOptions: ENTITY_ITEMS_DROPDOWN_LIMIT,
});

export const entityTypeFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'entityType',
  label: t('common.entity-type'),
  placeholder: t('common.entity-type'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const assigneeFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'assignee',
  label: t('common.assignee'),
  placeholder: t('common.assignee'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const creatorFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'creator',
  label: t('common.creator'),
  placeholder: t('common.creator'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const resolverFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'resolver',
  label: t('common.resolver'),
  placeholder: t('common.resolver'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const issueTypeFilter = (
  value: Ref<string[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter<string>({
  value,
  name: 'type',
  label: t('common.type'),
  placeholder: t('common.type'),
  options: issueTypeOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const todoOverdueAllFilter = (
  value: Ref<boolean | null>,
  reset: PaginationResetCallback = dummyCallback,
) => dropdownFilter<boolean | null>({
  value,
  name: 'overdue',
  label: t('common.overdue'),
  placeholder: t('common.overdue'),
  options: todoOverdueOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const inspectionOverdueAllFilter = (
  value: Ref<boolean | null>,
  reset: PaginationResetCallback = dummyCallback,
) => dropdownFilter<boolean | null>({
  value,
  name: 'overdue',
  label: t('common.overdue'),
  placeholder: t('common.overdue'),
  options: inspectionOverdueOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const issueOverdueAllFilter = (
  value: Ref<boolean | null>,
  reset: PaginationResetCallback = dummyCallback,
) => dropdownFilter<boolean | null>({
  value,
  name: 'overdue',
  label: t('common.overdue'),
  placeholder: t('common.overdue'),
  options: issueOverdueOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const issuePriorityTagFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'type',
  label: t('common.priority'),
  placeholder: t('common.priority'),
  options: issuePriorityOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const inspectorFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'inspector',
  label: t('common.inspector'),
  placeholder: t('common.inspector'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const userFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'user',
  label: t('common.user'),
  placeholder: t('common.user'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const entityFolderLabelFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'label',
  label: t('entity-folder.label'),
  placeholder: t('entity-folder.label'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const conductedByFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'conductedBy',
  label: t('inspections.conducted-by'),
  placeholder: t('inspections.conducted-by'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const sensorFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'sensor',
  label: t('common.sensor'),
  placeholder: t('common.sensor'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const questionTitleFilter = (
  value: Ref<string[]>,
  reset: PaginationResetCallback = dummyCallback,
) => freeTagFilter({
  value,
  name: 'questionTitle',
  label: t('inspections.questions'),
  placeholder: t('inspections.questions'),
  reset,
  hideLabel,
  horizontal,
});

export const responseFilter = (
  value: Ref<string[]>,
  reset: PaginationResetCallback = dummyCallback,
) => freeTagFilter({
  value,
  name: 'response',
  label: t('common.response'),
  placeholder: t('common.response'),
  reset,
  hideLabel,
  horizontal,
});

export const sectionFilter = (
  value: Ref<string[]>,
  options: SelectOptions<string>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'section',
  label: t('inspections.section'),
  placeholder: t('inspections.section'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const groupFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'group',
  label: t('common.group'),
  placeholder: t('common.group'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const roleFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'role',
  label: t('common.role'),
  placeholder: t('common.role'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const categoryFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'category',
  label: t('common.category'),
  placeholder: t('common.categories'),
  options,
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const checklistStatusFilter = (
  value: Ref<number | null>,
  reset: PaginationResetCallback = dummyCallback,
) => dropdownFilter<number | null>({
  value,
  name: 'status',
  label: t('common.status'),
  placeholder: t('common.status'),
  options: checklistStatusOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const checklistFilter = (
  value: Ref<number[]>,
  options: SelectOptions,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'checklist',
  label: t('common.checklist'),
  placeholder: t('common.checklist'),
  options,
  reset,
  hideLabel,
  horizontal,
});

export const issueStatusFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
  options?: SelectOptions<number>,
) => tagFilter({
  value,
  name: 'issueStatus',
  label: t('common.status'),
  placeholder: t('common.status'),
  options: options || issueStatusOptions(),
  reset,
  hideLabel,
  horizontal,
  isStackable,
});

export const inspectionStatusFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
) => tagFilter({
  value,
  name: 'status',
  label: t('common.status'),
  placeholder: t('common.status'),
  //Status.CantDo and Status.InProgress are not for Inspections
  options: inspectionStatusOptions(),
  reset,
  hideLabel,
  horizontal,
});

export const todoPriorityFilter = (
  value: Ref<boolean>,
  reset: PaginationResetCallback = dummyCallback,
) => checkboxFilter({
  value,
  name: 'priority',
  label: t('form.only-high-prio'),
  reset,
  hideLabel,
  horizontal,
});

export const todoOverdueCheckboxFilter = (
  value: Ref<boolean>,
  reset: PaginationResetCallback = dummyCallback,
) => checkboxFilter({
  value,
  name: 'overdue',
  label: t('form.only-overdue'),
  reset,
  hideLabel,
  horizontal,
});

export const notificationUnreadFilter = (
  value: Ref<boolean>,
  reset: PaginationResetCallback = dummyCallback,
) => checkboxFilter({
  value,
  name: 'unreadOnly',
  label: t('form.only-unread'),
  reset,
  hideLabel,
  horizontal,
});

export const autocompleteChecklistFilter = (
  value: Ref<number[]>,
  reset: PaginationResetCallback = dummyCallback,
) => checklistAutoComplete({
  value: value.value,
  disabled: false,
  isStackable,
  classes: 'is-horizontal',
}, {
  input: (input: number[]) => {
    reset();
    value.value = input;
  },
});
